import { createClient, type SupabaseClient } from '@supabase/supabase-js'

import { env, isSupabaseConfigured } from '@/lib/env.ts'

/**
 * The browser Supabase client.
 *
 * Created lazily and only when both URL and anon key are present. There is no
 * fallback client and no stub: when Supabase is not configured the application
 * must take the demo path explicitly rather than talk to something that
 * pretends to be a backend (AGENTS.md rule 2).
 *
 * Only the anon key is used here. It is publishable by design; the
 * service-role key must never appear in this file or in any VITE_* variable.
 */
let cached: SupabaseClient | null = null

export function getSupabaseClient(): SupabaseClient {
  if (!isSupabaseConfigured || env.VITE_SUPABASE_URL === undefined || env.VITE_SUPABASE_ANON_KEY === undefined) {
    throw new Error(
      'Supabase is not configured. Check isSupabaseConfigured before calling getSupabaseClient().',
    )
  }

  cached ??= createClient(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY, {
    auth: {
      // Links are bound to the browser that requested them (PKCE); an injected
      // implicit-token URL must not silently replace the current account.
      // The emailed code remains usable inside an installed iPhone app.
      flowType: 'pkce',
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  })

  return cached
}

/** Test seam: drops the memoised client so a test can swap configuration. */
export function resetSupabaseClientForTests(): void {
  cached = null
}
